import React, { memo, useState } from "react";
import { LazyLoadImage } from "react-lazy-load-image-component";
import styled, { css, keyframes } from "styled-components";
import { Transition, content, transparentize } from "@glossy";
import Icon from "@ui-kit/icon";

interface IProps {
  image: string;
  mediaFormat?: "picture" | "video";
  fallbackImage?: string;
  lazy?: boolean;
  animated?: boolean;
  onLoad?: (image: string) => void;
}

const appear = keyframes`
  from { opacity: 0; }
  to { opacity:1 }
`;

const LazyPicture = styled(({ success, animated, ...props }) => (
  <LazyLoadImage {...props} />
))`
  object-fit: cover;
  background-size: cover;
  background-position: center;
  width: 100%;
  height: 100%;
  animation: ${({ success, animated }) =>
    success &&
    animated &&
    css`
      ${appear} ${Transition.Length} ease forwards;
    `};
`;

const Picture = styled.div`
  background-color: ${(props) =>
    transparentize(0.5, content(props, "tertiary"))};
  background-position: center;
  object-fit: cover;
  height: 100%;
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const VideoOverlay = styled(Icon)`
  position: absolute;
  width: 100%;
  height: 24px;
  top: 50%;
  transform: translateY(-50%);
  text-align: center;
`;

const UiKitUtilsImage = memo(
  ({
    image,
    mediaFormat = "picture",
    fallbackImage,
    lazy,
    animated,
    onLoad,
  }: IProps) => {
    const [loadingImage, setloadingImage] = useState(image);
    const [validImage, setValidImage] = useState<string | null>(null);
    const [error, setError] = useState(false);

    const onImageLoaded = function () {
      setValidImage(loadingImage);
      onLoad?.(loadingImage);
    };
    const onFinalImageError = function (this: any) {
      this.onerror = null; // Prevent infinite loop
      setError(true);
    };

    // Image only used to verify if the url works
    const imageScout = new Image();
    imageScout.referrerPolicy = "no-referrer"; // Prevent request failing because of origin
    imageScout.onload = onImageLoaded;
    imageScout.onerror = function () {
      if (fallbackImage) {
        setloadingImage(fallbackImage);
        this.src = fallbackImage;
        this.onerror = onFinalImageError.bind(this);
      } else {
        onFinalImageError.call(this);
      }
    };

    const loadImage = () => {
      imageScout.src = image;
    };

    if (error) {
      onLoad?.("");
      // Couldn't load any image
      return mediaFormat === "video" ? (
        <Icon glyph="video-circle" size={24} />
      ) : (
        <Icon glyph="image" size={24} />
      );
    }
    //key is necessary so that LazyPicture properly reload if src change (fallback image)
    return (
      <ImageWrapper>
        {lazy ? (
          <LazyPicture
            key={validImage}
            src={validImage}
            success={!!validImage}
            animated={animated}
            beforeLoad={() => loadImage()}
          />
        ) : (
          <Picture style={{ backgroundImage: `url(${validImage})` }} />
        )}
        {mediaFormat === "video" && <VideoOverlay glyph="video-circle-bg" />}
      </ImageWrapper>
    );
  }
);

export default UiKitUtilsImage;
